import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// Dropdown menu width constant for boundary calculations
const DROPDOWN_WIDTH = 192; // 12rem = 192px (w-48)
const VIEWPORT_PADDING = 10; // Minimum padding from viewport edges
const MOBILE_BREAKPOINT = 768; // px

/**
 * Custom hook for rAF-throttled callback
 * Ensures the callback only fires once per animation frame
 */
const useThrottledCallback = (callback) => {
  const rafIdRef = useRef(null);
  const callbackRef = useRef(callback);
  
  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  const throttledFn = useCallback(() => {
    if (rafIdRef.current !== null) return; // Already scheduled
    
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      callbackRef.current();
    });
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);
  
  return throttledFn;
};

/**
 * Custom hook for body scroll lock on mobile
 */
const useBodyScrollLock = (isLocked) => {
  useEffect(() => {
    if (!isLocked) return;
    
    // Only lock on mobile
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (!isMobile) return;
    
    // Store original styles
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    const scrollY = window.scrollY;
    
    // Lock scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    return () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isLocked]);
};

/**
 * Portal component for rendering dropdowns outside the normal DOM flow.
 * This completely eliminates stacking context issues with hero images.
 * 
 * @param {React.ReactNode} children - The dropdown content to render
 * @param {boolean} isOpen - Whether the dropdown is open
 * @param {Function} onClose - Callback when clicking outside or pressing Escape
 * @param {React.RefObject} triggerRef - Reference to the trigger button for positioning
 * @param {string} align - Alignment of dropdown: 'left' or 'right' (default: 'right')
 * @param {boolean} lockScroll - Whether to lock body scroll on mobile (default: true)
 */
export const DropdownPortal = ({ 
  children, 
  isOpen, 
  onClose, 
  triggerRef,
  align = 'right',
  lockScroll = true
}) => {
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0 });
  const dropdownRef = useRef(null);
  
  // Lock body scroll on mobile when dropdown is open
  useBodyScrollLock(isOpen && lockScroll);

  // Calculate position based on trigger element with viewport boundary protection
  const updatePosition = useCallback(() => {
    if (!triggerRef?.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate base position
    let top = rect.bottom + 6; // 6px gap below trigger
    let left = rect.left;
    let right = viewportWidth - rect.right;
    
    // Get actual dropdown width if available
    const dropdownWidth = dropdownRef.current?.offsetWidth || DROPDOWN_WIDTH;
    const dropdownHeight = dropdownRef.current?.offsetHeight || 300;
    
    // Viewport boundary protection - horizontal
    if (align === 'right') {
      // Check if dropdown would go off left edge
      const effectiveLeft = viewportWidth - right - dropdownWidth;
      if (effectiveLeft < VIEWPORT_PADDING) {
        right = viewportWidth - dropdownWidth - VIEWPORT_PADDING;
      }
    } else {
      // Check if dropdown would go off right edge
      if (left + dropdownWidth > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - dropdownWidth - VIEWPORT_PADDING;
      }
      // Check if dropdown would go off left edge
      if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
      }
    }
    
    // Viewport boundary protection - vertical
    // If dropdown would go below viewport, position it above trigger
    if (top + dropdownHeight > viewportHeight - VIEWPORT_PADDING) {
      top = rect.top - dropdownHeight - 6;
      // If still off-screen, just use max available space
      if (top < VIEWPORT_PADDING) {
        top = VIEWPORT_PADDING;
      }
    }
    
    setPosition({
      top,
      left,
      right,
      width: rect.width,
      triggerWidth: rect.width,
    });
  }, [triggerRef, align]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Create throttled position update for scroll performance
  const throttledUpdatePosition = useThrottledCallback(updatePosition);

  // Update position when open state changes or on scroll/resize
  useLayoutEffect(() => {
    if (!isOpen) return;

    // Initial position calculation (immediate, not throttled)
    updatePosition();

    // Listen on all scroll events (capture phase for nested scrollers)
    // Use throttled callback to prevent layout thrashing
    window.addEventListener('scroll', throttledUpdatePosition, { passive: true, capture: true });
    window.addEventListener('resize', throttledUpdatePosition, { passive: true });
    
    // Also listen for orientation change on mobile
    window.addEventListener('orientationchange', throttledUpdatePosition);
    
    // Also update on any DOM scroll (for scrollable containers)
    document.addEventListener('scroll', throttledUpdatePosition, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', throttledUpdatePosition, { capture: true });
      window.removeEventListener('resize', throttledUpdatePosition);
      window.removeEventListener('orientationchange', throttledUpdatePosition);
      document.removeEventListener('scroll', throttledUpdatePosition, { capture: true });
    };
  }, [isOpen, updatePosition, throttledUpdatePosition]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking the trigger
      if (triggerRef?.current?.contains(event.target)) {
        return;
      }
      // Don't close if clicking inside the dropdown
      if (dropdownRef.current?.contains(event.target)) {
        return;
      }
      onClose();
    };

    // Handle Escape key
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
        // Return focus to trigger
        triggerRef?.current?.focus();
      }
    };

    // Use capture phase for better iOS Safari compatibility
    // Small delay to prevent immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, { capture: true });
      document.addEventListener('touchstart', handleClickOutside, { capture: true, passive: true });
    }, 10);
    
    document.addEventListener('keydown', handleEscape);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
      document.removeEventListener('touchstart', handleClickOutside, { capture: true });
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!mounted || !isOpen) return null;

  const dropdownStyle = {
    position: 'fixed',
    top: `${position.top}px`,
    ...(align === 'right' 
      ? { right: `${position.right}px` } 
      : { left: `${position.left}px` }
    ),
    zIndex: 999999,
  };

  return createPortal(
    <div 
      ref={dropdownRef}
      className={`dropdown-portal-root ${isVisible ? 'dropdown-visible' : ''}`}
      style={dropdownStyle}
      data-testid="dropdown-portal"
    >
      {children}
    </div>,
    document.body
  );
};

/**
 * Hook to manage dropdown state with portal support
 * @returns {Object} { isOpen, setIsOpen, triggerRef, close }
 */
export const usePortalDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return { isOpen, setIsOpen, triggerRef, close, toggle };
};

export default DropdownPortal;
